import BaseModule from './BaseModule';
import Factory from '../libs/Factory';
import debounce from '../libs/Debounce';
import { splitKeyValue } from '../libs/Helpers';
import { setOptions, importLibrary } from "@googlemaps/js-api-loader";

declare global {
    interface Window {
        hbspt: any;
    }
}
declare var hbspt: any;
declare var EXPERIAN_KEY: string;
declare var fbq: Function;
declare var ISUZU_MODELS: Record<string, Model> | undefined;

type AddressSuggestion = {
    global_address_key: string;
    text: string;
};
type AddressDetail = {
    address_line_1: string;
    address_line_2: string;
    locality: string;
    postal_code: string;
}
type Dealer = Record<string, any>;
type Model = Record<string, string|boolean>;
type Position = {
    lat: number;
    lng: number;
}
type CurrentLocation = {
    address: string;
    postcode: string;
    position?: google.maps.LatLng;
}

const DEALER_FIELD = 'isu_uk_select_dealer';
const MODEL_FIELD = 'isu_uk_select_model';
const MILES_IN_METER = 0.0006213711922373339;
const ALLOWED_DEALER_VALUES = ['true', 'ev', 'at35', 'service', 'all'];
const FORM_STYLES = (color: string, hideLabels: boolean) => `<style>
    .field { position: relative; }
    .address-results {
        display: none;
        flex-direction: column;
        width: 100%;
        max-height: 200px;
        overflow-y: scroll;
        position: absolute;
        top: 100%;
        left: 0;
        z-index: 999;
        background-color: #ffffff;
    }
    .address-results .address {
        display: block;
        width: 100%;
        padding: .5rem 1rem;
        background-color: #ffffff;
        font-family: arial, sans-serif;
        color: #000000 !important;
        text-decoration: none;
    }
    .address-results .address:hover { background-color: #dcdcdc; }
    .preferreddealer-results {
        display: flex;
        flex-direction: column;
        flex-wrap: nowrap;
        margin-top: 1rem;
        font-family: arial, helvetica, sans-serif;
        p {
            display: flex;
            margin: 0;
        }
        .badges { margin-left: auto; }
        .badge {
            padding: .25rem .5rem;
            border-radius: .25rem;
            color: #ffffff;
            font-size: .875rem;
            
            &.ev { background-color: #53a9da; }
            &.at35 { background-color: #52525e; }
            &.ice { background-color: #000000; color: #ffffff; }
        }
        label.preferreddealer {
            border: 1px solid #dcdcdc;
            border-radius: .25rem;
            padding: .5rem 1rem;
            cursor: pointer;
        }
        .preferreddealer-title {
            width: 100%;
            display: flex;
            justify-content: space-between;
            margin: 0;
        }
        input {
            display: none;
            &:checked + label { border: 1px solid #e30513; }
        }
    }
    .submitted-message {
        padding: 1rem;
    }
    ${color ? `form.hs-form .hs-form-field label:not(.hs-error-msg), form.hs-form .legal-consent-container .hs-richtext, .preferreddealer-results, .hbspt-form .submitted-message { color: ${color} }` : ''}
    ${hideLabels ? 'form.hs-form .hs-form-field label:not(.hs-error-msg) { display: none }' : ''}
</style>`;

export function haversine_distance(mk1: Position, mk2: Position) {
    const R = 3958.8; // Radius of the Earth in miles
    const rLat1 = mk1.lat * (Math.PI/180); // Convert degrees to radians
    const rLat2 = mk2.lat * (Math.PI/180); // Convert degrees to radians
    const diffLat = rLat2-rLat1; // Radian difference (latitudes)
    const diffLon = (mk2.lng-mk1.lng) * (Math.PI/180); // Radian difference (longitudes)
    return 2 * R * Math.asin(Math.sqrt(Math.sin(diffLat/2)*Math.sin(diffLat/2)+Math.cos(rLat1)*Math.cos(rLat2)*Math.sin(diffLon/2)*Math.sin(diffLon/2)));
}
function positionFromLatLng(latLng: google.maps.LatLng): Position {
    return {
        lat: latLng.lat(),
        lng: latLng.lng()
    };
}
function positionFromDealer(dealer: Dealer): Position {
    return {
        lat: dealer.lat,
        lng: dealer.lng
    };
}
async function fetchDealers(): Promise<Dealer[]> {
    const query = new URLSearchParams({action: 'preferred_dealer' });
    const response = await fetch(`/wp-admin/admin-ajax.php?${query.toString()}`, {
        method: 'GET', headers: { 'Accept': 'application/json' },
    });
    const dealers: Dealer[] = await response.json();
    dealers.forEach((dealer) => {
        dealer.real_distance = {};
    });
    return dealers;
}
async function loadGeoLibs(mapsKey: string) {
    setOptions({v: "weekly", key: mapsKey, libraries: ["geocoding","routes"]});
    const [{Geocoder}, {Route}] = await Promise.all([
        importLibrary("geocoding"),
        importLibrary("routes"),
    ]);
    return {geocoder: Geocoder, routes: Route};
}
export async function experianAddressSuggestions(address: string): Promise<AddressSuggestion[]> {
    if (!EXPERIAN_KEY) {
        console.error("No experian key supplied...");
        return [];
    }
    if (!address) {
        console.error("No address supplied...");
        return [];
    }

    const json = await fetch('https://api.experianaperture.io/address/search/v1', {
        method: 'POST',
        headers: {
            'accept': 'application/json',
            'Auth-Token': EXPERIAN_KEY,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            'country_iso': 'GBR',
            'datasets': [ 'gb-address' ],
            'max_suggestions': 100,
            'components': { 'unspecified': [ address ] }
        }),
    });
    const response: {result: {suggestions: AddressSuggestion[]}} = await json.json();
    if (!response.result.suggestions) {
        console.error("Error fetching address suggestions...");
        return [];
    }

    return response.result.suggestions;
}
export async function experianAddressDetails(address_key: string): Promise<AddressDetail | false> {
    if (!EXPERIAN_KEY) {
        console.error("No experian key supplied...");
        return false;
    }
    if (!address_key) {
        console.error("No address key supplied...");
        return false;
    }

    const json = await fetch('https://api.experianaperture.io/address/format/v1/' + address_key, {
        method: 'GET',
        headers: {
            'accept': 'application/json',
            'Auth-Token': EXPERIAN_KEY,
            'Content-Type': 'application/json'
        },
    });
    const response: {result: {address: AddressDetail}} = await json.json();
    if (!response.result.address) {
        console.error("Error fetching address details...");
        return false;
    }

    return response.result.address;
}

// TODO: show errors on fields/form?
// TODO: loading animations
export default class HsForm extends BaseModule {
    declare el: HTMLDivElement;
    form: HTMLFormElement;
    private readonly defaults: Record<string, string>;
    private readonly hide: string[] = [];

    private readonly hasAddress: boolean = false;
    address_line_1?: HTMLInputElement;
    address_line_2?: HTMLInputElement;
    locality?: HTMLInputElement;
    postal_code: HTMLInputElement;
    addressFields: HTMLInputElement[] = [];

    autocomplete?: HTMLInputElement;
    addressWrapper: HTMLDivElement;
    currentLocation: CurrentLocation | null;

    private readonly hasDealer: boolean = false;
    private readonly dealerFilter: string | null; // default to 'ice' as in all options: ev,at35
    currentDealer: Dealer | null;
    dealerWrapper: HTMLDivElement;
    dealers: Dealer[] = [];
    dealersInitialized: boolean = false;

    private readonly hasModel: boolean = false;
    private readonly modelFilter: string | null;
    modelInput?: HTMLInputElement | HTMLSelectElement;
    modelWrapper: HTMLDivElement;
    models: Record<string, Model> = {};

    geocoder: typeof google.maps.Geocoder | null;
    routes: typeof google.maps.routes.Route | null;

    constructor(el: HTMLDivElement, factory: Factory) {
        super(el, factory, 'HsForm');

        const dataset = this.el.dataset;
        const formId = dataset.formId;
        if (!formId) {
            console.error("FormID missing...");
            return;
        }
        const defaults: string = dataset.defaults ?? '';
        defaults && (this.defaults = splitKeyValue(defaults));
        const hide: string = dataset.hide ?? '';
        hide && (this.hide = hide.split(','));
        const color: string = dataset.color ?? '';
        const hideLabels: boolean = (dataset.hideLabels ?? '') === 'true';

        this.hasAddress = dataset.address === 'true';

        this.hasModel = !!dataset.models;
        this.hasModel && (this.modelFilter = dataset.models && dataset.models !== 'true' ? dataset.models : null);
        if (typeof ISUZU_MODELS !== 'undefined') {
            this.models = ISUZU_MODELS;
        }

        this.hasDealer = ALLOWED_DEALER_VALUES.includes(dataset.dealers ?? '');
        this.hasDealer && (this.dealerFilter = dataset.dealers && dataset.dealers !== 'true' ? dataset.dealers : null);

        hbspt.forms.create({
            portalId: '139487560',
            formId: formId,
            target: '#hs-form-' + formId,

            onFormReady: ($form: JQuery<HTMLFormElement>) => {
                this.form = $form[0] as HTMLFormElement;
                if (!this.form) {
                    console.error("Form missing...");
                    return;
                }
                this.form.closest('html')?.querySelector('head')?.insertAdjacentHTML(
                    "beforeend",
                    FORM_STYLES(color, hideLabels)
                );
                this.initForm();
            },
            onFormSubmitted: () => {
                // TODO: send to our wp table?
                if(typeof fbq === 'function') {
                    fbq('track', 'Lead');
                }

                if (this.hasModel && this.modelWrapper) {
                    this.modelWrapper.classList.add('hidden');
                }
            },
        });
    }

    initForm(){
        this.defaults && Object.entries(this.defaults).forEach(([key, value]) => {
            const input = this.form.querySelector<HTMLInputElement|HTMLSelectElement>(`[name="${key}"]`);
            input && (input.value = value) && input.dispatchEvent(new Event('input', {bubbles: true}));
        });
        this.hide && this.hide.forEach((name) => {
            const toHide = this.form.querySelector(`[name="${name}"]`)?.closest<HTMLElement>('.field');
            toHide && (toHide.style.display = 'none');
        });

        this.address_line_1 = this.form.querySelector('[autocomplete="address-line1"]') as HTMLInputElement;
        this.form.querySelector('[name="address_line_2"]')?.setAttribute('autocomplete','address-line2');
        this.address_line_2 = this.form.querySelector('[autocomplete="address-line2"]') as HTMLInputElement;
        this.locality = this.form.querySelector('[autocomplete="address-level2"]') as HTMLInputElement;
        this.postal_code = this.form.querySelector('[autocomplete="postal-code"]') as HTMLInputElement;
        // TODO: check if address can not have 1st line
        this.addressFields = [this.address_line_1, this.locality, this.postal_code];
        this.autocomplete = this.address_line_1;
        if (this.hasAddress && this.autocomplete) {
            this.initAddress();
        }

        // TODO: when changing the select on form the model picker does not update
        // TODO: make readonly if we have a picker???
        const modelInput = this.form.querySelector<HTMLSelectElement>('[name="' + MODEL_FIELD + '"]');
        if (this.hasModel && modelInput) {
            this.initModels(modelInput);
        }

        const dealerInput = this.form.querySelector('[name="' + DEALER_FIELD + '"]') as HTMLInputElement;
        if (this.hasDealer && dealerInput) {
            this.initDealers(dealerInput).then(() => {
                // setup blur event on address to show dealers if all address fields have values && no autocomplete support
                !this.hasAddress && this.addressFields.forEach((field) => {
                    field.addEventListener('blur', async () => {
                        if (this.addressFields.every((field) => !!field?.value)) {
                            await this.apiLocation(this.postal_code.value, this.addressFromInputs());
                            await this.dealerListing();
                        }
                    });
                });
                this.modelInput?.addEventListener('input', debounce(async () => {
                    await this.dealerListing();
                }, 500));
            });
        }
    }

    initAddress() {
        if(!this.autocomplete){
            return;
        }
        this.addressWrapper = document.createElement('div');
        this.addressWrapper.classList.add('address-results');
        this.addressWrapper.addEventListener('click', async (event) => {
            event.preventDefault();
            const target = event.target as HTMLLinkElement;
            const address_key = target.dataset.key ?? '';
            if (address_key) {
                const address = await experianAddressDetails(address_key);
                if (!address) {
                    this.hasDealer && this.dealerSelect();
                    return;
                }
                this.setAddress(address);

                if (this.hasDealer) {
                    await this.apiLocation(address.postal_code, this.addressFromDetails(address));
                    await this.dealerListing();
                }
            }
            this.addressWrapper.style.display = 'none';
        });

        this.autocomplete.after(this.addressWrapper);
        this.autocomplete.placeholder = 'Start typing your address...';
        this.autocomplete.addEventListener('input', debounce(async () => {
            const value = this.autocomplete!.value;
            if (value && value.length > 2) {
                const address = await experianAddressSuggestions(value);
                if (!address || !address.length) {
                    // this is fine, we clear and allow user to fill in manually
                    this.addressWrapper.innerHTML = '';
                    return;
                }

                const list = address.map((item: any) =>
                    `<a href="#" class="address" data-key="${item.global_address_key}">${item.text}</a>`
                ).join('');

                if (list !== '') {
                    this.addressWrapper.style.display = 'flex';
                    this.addressWrapper.innerHTML = list;
                }
            }
        }, 500));
    }

    initModels(modelInput: HTMLSelectElement) {
        this.modelInput = modelInput;
        if (this.modelInput.tagName === 'SELECT') {
            const providedModels = new Set<string>(
                Array.from((this.modelInput as HTMLSelectElement)!.options).map((option) => option.value)
            );
            console.log('Models provided: ', providedModels);

            if (this.modelFilter && this.models) {
                const filters = this.modelFilter.split(',');
                console.log('Model filters:', filters);
                Array.from(this.modelInput.options).forEach((option) => {
                    if (!option.value) { return; }
                    const model = this.models[option.value];
                    // check if flags on model match at least 1 of filters
                    if (model && filters.some((filter) => model[filter])) {
                        return;
                    }
                    option.remove();
                });

                const filteredModels = new Set<string>(
                    Array.from((this.modelInput as HTMLSelectElement)!.options).map((option) => option.value)
                );
                console.log('Models provided: ', filteredModels);

                return;
            }
        }

        const modelWrapper = this.el.querySelector<HTMLDivElement>('.model-picker');
        if (!modelWrapper) {
            console.warn("Model picker HTML missing...");
            return;
        }
        this.modelWrapper = modelWrapper;
        const usedModels = this.initModelPicker();
        if(usedModels){
            console.log('Models picker: ', usedModels);
        }
    }

    initModelPicker(): Set<string> | undefined {
        const modelSelector = this.modelWrapper.querySelector<HTMLDivElement>('.model-selector');
        if (!modelSelector) {
            console.warn("Model selector missing...");
            return;
        }

        const toggleModelsByFuel = (value: string) => {
            modelSelector.classList.toggle('ev', value === 'ev');
            modelSelector.classList.toggle('ice', value === 'ice');
        };
        const setModelInput = (modelValue: string) => {
            this.modelInput!.value = modelValue;
            this.modelInput!.dispatchEvent(new Event('input'));
        }

        let usedModels = new Set<string>();
        const initialModel = this.modelInput!.value;
        const modelItems = this.modelWrapper.querySelectorAll<HTMLDivElement>('.model-selector-item');
        modelItems.forEach((item) => {
            const modelValue = item.dataset.id ?? '';
            modelValue && usedModels.add(modelValue);
            const fuelType = item.dataset.type ?? '';
            item.addEventListener('click', () => {
                setModelInput(modelValue);
            });

            // match from form init values click on active one
            if (modelValue === initialModel) {
                toggleModelsByFuel(fuelType);
                item.click();
            }
        });
        if (!initialModel) {
            setModelInput(modelItems[0]?.dataset.id ?? '');
        }

        // ice|ev toggle buttons
        const fuelToggles = this.modelWrapper.querySelectorAll<HTMLButtonElement>('.fuel-toggle');
        fuelToggles?.forEach((toggle) => {
            const fuelType = toggle.dataset.type ?? '';
            toggle.addEventListener('click', () => {
                if (!modelSelector.classList.contains(fuelType)) {
                    toggleModelsByFuel(fuelType);
                    fuelToggles.forEach((t) => {t.classList.toggle('active', t === toggle)});
                    this.modelWrapper.querySelector<HTMLDivElement>(`.model-selector-item.${fuelType}`)?.click();
                }
            });
        });

        if (window.location.hash) {
            const hash = window.location.hash.replace('#', '');
            const fuelToggle = [...fuelToggles].find(t => t.dataset.type === hash);
            if(fuelToggle) {
                fuelToggle.click();
                return;
            }

            const model = [...modelItems].find(t => t.dataset.title === hash);
            if(model) {
                toggleModelsByFuel(model.dataset.type!);
                fuelToggles.forEach((t) => {t.classList.toggle('active', t.dataset.type === model.dataset.type)});
                model.click();
            }
        }

        return usedModels;
    }

    async initDealers(dealerInput: HTMLInputElement){
        // TODO: this replaces the original field!!! should we rather set to hidden and append radios?
        const dealerWrapper = dealerInput?.closest<HTMLDivElement>('.input');
        if(!dealerWrapper){
            console.warn('Dealer wrapper not found');
            return;
        }
        this.dealerWrapper = dealerWrapper;
        this.dealerWrapper.innerHTML = '<p>Fill in your address details and we will show you your nearest Isuzu dealers</p>';
        this.dealerWrapper.classList.add('preferreddealer-results');
        this.dealers = await fetchDealers();
        if(!this.dealers || !this.dealers.length){
            console.error('Dealers not found');
            return;
        }

        const mapKey = this.el.dataset.mapkey;
        if (!mapKey) {
            console.error('Map key not found, cannot initialize dealer distance');
            this.dealerSelect();
            return;
        }
        const geoLibs = await loadGeoLibs(mapKey);
        this.geocoder = geoLibs.geocoder;
        this.routes = geoLibs.routes;
        if (!(this.geocoder && this.routes)){
            console.error('Geocoder or routes not found, cannot initialize dealer distance');
            this.dealerSelect();
            return;
        }
        this.dealersInitialized = true;

        const dealerAccount = dealerInput.value;
        if (dealerAccount) {
            const dealer = this.dealers.find((dealer) => dealer.account === dealerAccount);
            dealer && (this.currentDealer = dealer);
        }

        // check for existing address to prefill dealers
        const address = this.addressFromInputs();
        if (address) {
            await this.apiLocation(this.postal_code.value ?? '', address);
            await this.dealerListing();
            // we don't have an address so let's just show the current dealer if we have one
        } else if (this.currentDealer) {
            this.dealerWrapper.innerHTML = this.dealerRadio(this.currentDealer, true);
        }
    }

    async dealerListing() {
        if (!this.dealersInitialized) {
            console.warn('Dealers not initialized...');
            return;
        }
        if (!this.currentLocation) {
            return;
        }

        let dealers = Array.from(this.dealers); // clone dealers
        let flag = 'new_sales';
        if (this.hasModel && this.modelInput) {
            const model = this.modelInput.value || '';
            switch (model) {
                case "D-MAX Arctic Trucks AT35":
                    flag = 'arctic_trucks_dealer'; break;
                case 'D-MAX eDL40':
                case 'D-MAX eV-Cross':
                    flag = 'ev_dealer'; break;
            }
        }

        // TODO: hmm... who should have precedence?
        // let dealer_type = [1,2,6]; // dealers have sales
        switch (this.dealerFilter) {
            case "all":
                flag = ''; break;
            case "service":
                flag = 'service'; break;
            case "at35":
                flag = 'arctic_trucks_dealer'; break;
            case "ev":
                flag = 'ev_dealer'; break;
        }
        dealers = dealers.filter(
            (dealer) =>  (!flag || !!dealer[flag]) // dealer_type.includes(Number(dealer.dealer_type))
        );

        dealers.forEach((dealer) => {
            dealer.distance = haversine_distance(
                positionFromLatLng(this.currentLocation!.position!), positionFromDealer(dealer)
            );
        });
        dealers = dealers.sort((a, b) => a.distance - b.distance).slice(0, 9);

        // actual route distance sort for top 5
        if (this.routes) {
            const currentLocation = this.currentLocation;
            dealers = await Promise.all(dealers.map(async (dealer) => {
                // check cache
                if(dealer.real_distance && dealer.real_distance[currentLocation.postcode]) {
                    dealer.distance = dealer.real_distance[currentLocation.postcode];
                    return dealer;
                }
                const distance = await this.apiDistance(
                    currentLocation.address,
                    this.addressFromDealer(dealer, true)
                );
                if (distance) {
                    dealer.distance = distance;
                    // set cache
                    const masterDealer = this.dealers.find((d) => d.account === dealer.account);
                    masterDealer && (masterDealer.real_distance[currentLocation.postcode] = distance);
                }
                return dealer;
            }));
        }
        dealers = dealers.sort((a, b) => a.distance - b.distance).slice(0, 5);

        // generate dealer picker HTML
        const currentDealer = this.dealerWrapper.querySelector<HTMLInputElement>('input[name="' + DEALER_FIELD + '"]:checked')?.value ?? '';
        this.dealerWrapper.innerHTML = dealers.map(
            (dealer) => this.dealerRadio(dealer, (!!currentDealer && dealer.account === currentDealer))
        ).join('');
    }

    dealerSelect(): void {
        if (!this.dealerWrapper || !(this.dealers && this.dealers.length)){
            return;
        }

        let dealerSelect = `<select name="${DEALER_FIELD}" class="hs-input"><option value="">Select a dealer</option>`;
        dealerSelect += this.dealers.map((dealer) =>
            `<option ${dealer.account === this.currentDealer?.account ? 'selected' : ''} value="${dealer.account}">${dealer.name} - ${dealer.address_postalcode}</option>`
        ).join('');
        dealerSelect += '</select>';
        this.dealerWrapper.innerHTML = dealerSelect;
    }

    dealerRadio(dealer: Dealer, selected: boolean = false): string {
        const tags = [
            '<span class="badge ice" title="Dealer sells diesel vehicles">DIESEL</span>',
            dealer.ev_dealer ?
                '<span class="badge ev" title="Dealer sells electric vehicles">EV</span>':'',
            dealer.arctic_trucks_dealer ?
                '<span class="badge at35" title="Dealer sells Arctic Trucks">AT35</span>':'',
        ].filter(Boolean).join(' ');

        return `
<input id="${dealer.dealer_id}" type="radio" name="${DEALER_FIELD}" value="${dealer.account}" ${selected ? 'checked' : ''}>
<label class="preferreddealer" for="${dealer.dealer_id}">
    <h5 class="preferreddealer-title"><span>${dealer.name}</span> ${dealer.distance ? `<small>(${dealer.distance.toFixed(1)}miles)</small>` : ''}</h5>
    <p>${this.addressFromDealer(dealer, true)} <span class="badges">${tags}</span></p>
</label>
        `;
    }

    async apiDistance(origin: string, destination: string) {
        if (!this.routes) {
            return 0;
        }

        const {routes} = await this.routes!.computeRoutes({
            origin, destination, fields: ["distanceMeters"],
        });
        if(!routes || !routes[0]?.distanceMeters){
            console.error(`No route found to dealer...`);
            return 0;
        }

        return routes[0].distanceMeters * MILES_IN_METER;
    }

    async apiLocation(postcode: string, address: string) {
        if (this.currentLocation && this.currentLocation.address === address && this.currentLocation.position) {
            return;
        }

        if (!this.geocoder) {
            console.error("Missing Geocoder...");
            this.currentLocation = null;
            return;
        }

        const geocoder = new this.geocoder();
        const response = await geocoder.geocode({ address, region: 'GB', });
        const { results } = response;
        const position = results[0].geometry.location;

        if (!position) {
            console.error("Error Geocoding...");
            this.currentLocation = null;
            return;
        }

        this.currentLocation = {address, postcode, position};
    }

    setAddress(address: AddressDetail) {
        // don't trigger input on autocomplete field as it would loop
        if (this.address_line_1) {
            this.address_line_1.value = address.address_line_1;
            this.address_line_1.dispatchEvent(new Event('change', {bubbles: true}));
        }
        if (this.address_line_2) {
            this.address_line_2.value = address.address_line_2;
            this.address_line_2.dispatchEvent(new Event('input', {bubbles: true}));
            this.address_line_2.dispatchEvent(new Event('change', {bubbles: true}));
        }
        if (this.locality) {
            this.locality.value = address.locality;
            this.locality.dispatchEvent(new Event('input', {bubbles: true}));
            this.locality.dispatchEvent(new Event('change', {bubbles: true}));
        }
        if (this.postal_code) {
            this.postal_code.value = address.postal_code;
            if (this.address_line_1) {
                this.postal_code.dispatchEvent(new Event('input', {bubbles: true}));
            }
            this.postal_code.dispatchEvent(new Event('change', {bubbles: true}));
        }
    }

    addressFromDetails(address: AddressDetail): string {
        return [
            address.address_line_1, address.address_line_2, address.locality, address.postal_code
        ].filter(Boolean).join(', ');
    }

    addressFromInputs(): string {
        return [
            this.address_line_1?.value ?? '',
            this.address_line_2?.value ?? '',
            this.locality?.value ?? '',
            this.postal_code.value ?? '',
        ].filter(Boolean).join(', ');
    }

    addressFromDealer(dealer: Dealer, inline = false): string {
        return [[
            dealer.address_street1, dealer.address_street2, dealer.address_street3
        ].filter(Boolean).join(', '),
            dealer.address_town,
            dealer.address_postalcode
        ].filter(Boolean).join(inline ? ", " : ", <br>");
    }
}